import { useEffect } from 'react';

import { getUserSessionData } from '@/Application/Hooks/GetCustomerData';
import { useAppSelector } from '@/Application/Hooks/Redux';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks';
import {
  GtmEvent,
  GtmPageName,
  GtmPagePath,
  GtmTypeUser
} from '@/modules/perseo-billing/interfaces/src/front';
import { notifyError } from '@/modules/perseo-billing/services/error-handler';
import { getGtmPageVirtualEvent } from '@/modules/perseo-billing/utils';
import { sendEventByDevice } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBillingEvents';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

const ORDER_TYPE_FAST_CHECKOUT = 'FastCheckout';

const checkIsFastCheckout = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  const orderType = params.get('orderType');
  return orderType === ORDER_TYPE_FAST_CHECKOUT;
};

const getTypeUser = (): GtmTypeUser => {
  const sessionData = getUserSessionData();
  return sessionData ? GtmTypeUser.Registered : GtmTypeUser.Guest;
};

const getPageName = (): GtmPageName => {
  const isFastCheckout = checkIsFastCheckout();
  return isFastCheckout
    ? GtmPageName.FastCheckoutConfirmation
    : GtmPageName.CheckoutConfirmation;
};

const getEventOrderConfirmationPage = () => {
  const pageName = getPageName();
  const typeUser = getTypeUser();

  const payload = getGtmPageVirtualEvent({
    page: GtmPagePath.CheckoutConfirmation,
    pagename: pageName,
    typeUser: typeUser
  });

  return {
    eventName: GtmEvent.PageVirtual,
    payload: payload
  };
};

const sendOrderConfirmationPageEvent = (): void => {
  const event = getEventOrderConfirmationPage();
  sendEventByDevice(event);
};

const useGtmEvents = () => {
  const paymentSuccess = useAppSelector(
    (state) => state?.billing?.paymentSuccess
  );
  const {
    customer: { data: customerData }
  } = useCustomerContext();
  const isRegistered = customerData !== null;

  const basketSummary = paymentSuccess?.basketSummary;
  const customer = paymentSuccess?.customer;

  const isNotReady = isRegistered
    ? !customer || !basketSummary || !customerData
    : !basketSummary || !customer;

  const { sendProductsFromOrderConfirmationGtm, sendCheckoutUserGtm } =
    useGtmBillingProduct();

  useEffect(() => {
    try {
      sendOrderConfirmationPageEvent();
    } catch (error) {
      notifyError(error);
    }
  }, []);

  useEffect(() => {
    if (isNotReady) {
      return;
    }

    sendCheckoutUserGtm();
    sendProductsFromOrderConfirmationGtm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotReady]);
};

export default useGtmEvents;
